// [목적] /collections/:id 컬렉션 상세 페이지 — 실데이터 연결 + cursor 무한 스크롤 + ErrorBoundary
// [이유] FEED-03 — useCollection(id) + useCollectionItems(id) 실데이터 연결.
//         비공개 컬렉션 403/404 → ErrorBoundary fallback + 로그인 유도 CTA(guest).
//         IntersectionObserver 기반 무한 스크롤로 아이템 페이지 로드
//         B3(FlowLine 04d69112): 정렬 드롭다운 추가 — GET /collections/:id/items 는 cursor/limit 만
//         지원하고 sort 파라미터가 없음(apps/api collections.controller.ts listItems 확인) →
//         클라이언트 정렬(현재까지 로드된 페이지 한정)로 구현.
// [사이드 이팩트] useCollection throwOnError=true → 403/404 시 ErrorBoundary 캐치.
//                 아이템 목록 무한 스크롤 — sentinelRef IntersectionObserver 로 fetchNextPage 트리거
//                 정렬 변경은 이미 로드된 allItems 배열만 재정렬 — 다음 페이지를 더 불러오면 새로 합쳐진
//                 뒤 다시 정렬된다(서버 페이지 경계와 무관하게 항상 클라이언트 배열 전체 기준).
// [수정 시 고려사항] like/bookmark/comment onClick 실배선 금지 (후속 fe-folio-social-wire).
//                   컬렉션 팔로우(follow collection) 기능은 BE 미구현 — 버튼은 UI 전용으로 유지.
//                   private 컬렉션 비큐레이터 접근 시 BE 가 404 반환 (R-9 정보 누설 방지)
//                   ponytail: BE 가 sort 파라미터를 지원하게 되면 useCollectionItems 에 직접 전달하는
//                   서버 정렬로 교체하고 이 클라이언트 정렬은 제거할 것.

import { useEffect, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  CircularProgress,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import { useNavigate, useParams } from 'react-router-dom';

import { ErrorBoundary } from '../components/feedback/ErrorBoundary.js';
import { CollectionItemSkeleton } from '../components/feedback/Skeleton.js';
import { useAuth } from '../hooks/useAuth.js';
import { useCollection, useCollectionItems } from '../hooks/useCollection.js';
import type { CollectionDetail, CollectionItemPreview } from '@gangwoncanvas/types';

type ItemSortOption = 'default' | 'title';

const SORT_LABEL: Record<ItemSortOption, string> = {
  default: '최신순',
  title: '이름순',
};

/**
 * [목적] 컬렉션 아이템 클라이언트 정렬 — BE sort 파라미터 미지원(ponytail, 파일 헤더 주석 참조)
 * [이유] title 정렬은 로케일(가나다) 비교, default 는 서버가 내려준 순서(ord/addedAt) 그대로 유지.
 * [사이드 이팩트] 없음(순수 함수).
 * [수정 시 고려사항] BE sort 지원 시 이 함수 삭제하고 useCollectionItems 에 파라미터 전달로 교체.
 */
function sortItems(items: CollectionItemPreview[], sort: ItemSortOption): CollectionItemPreview[] {
  if (sort === 'title') {
    return [...items].sort((a, b) => (a.title ?? '').localeCompare(b.title ?? '', 'ko'));
  }
  return items;
}

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const INK_LOW = c.ink.low[L];
const BORDER = c.line.base[L];
const PAPER1 = c.paper[1][L];
const BRAND = c.brand[500][L];

// ─── CollectionPage ────────────────────────────────────────────────────────────

/**
 * [목적] /collections/:id 페이지 진입점 — ErrorBoundary 래핑
 * [이유] FEED-03 — 403/404 ApiError 를 ErrorBoundary 가 처리하도록 래핑
 * [사이드 이팩트] isGuest 전달 → 401/403 fallback 에서 로그인 CTA 표시
 * [수정 시 고려사항] ErrorBoundary 가 throwOnError=true 훅의 에러를 캐치
 */
export function CollectionPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { status } = useAuth();

  return (
    <ErrorBoundary isGuest={status === 'guest'} loginHref={`/login?returnTo=/collections/${id}`}>
      <CollectionDetail id={id} />
    </ErrorBoundary>
  );
}

// ─── CollectionDetail ─────────────────────────────────────────────────────────

/**
 * [목적] 컬렉션 상세 본문 — 커버 + 큐레이터 + 아이템 무한 스크롤
 * [이유] FEED-03 실데이터 연결 — useCollection + useCollectionItems
 * [사이드 이팩트] throwOnError=true → 403/404 시 상위 ErrorBoundary 전파
 * [수정 시 고려사항] 컬렉션 팔로우 버튼은 UI 전용 (BE 미구현)
 */
function CollectionDetail({ id }: { id: string | undefined }): JSX.Element {
  const navigate = useNavigate();
  const [sort, setSort] = useState<ItemSortOption>('default');
  const { collection, isLoading: collectionLoading, error: collectionError } = useCollection(id);
  // [목적] throwOnError=false 인 useCollection 의 에러를 상위 ErrorBoundary 로 전파
  // [이유] 페이지 레벨에서 throw 시점을 명시해야 React 가 ErrorBoundary 로 위임함
  // [사이드 이팩트] 렌더 도중 throw → ErrorBoundary fallback UI 가 표시됨
  // [수정 시 고려사항] 페이지 단위 에러는 항상 ErrorBoundary 로 위임. 인라인 메시지 원하면 이 throw 를 제거
  if (collectionError) throw collectionError;
  const {
    pages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: itemsLoading,
  } = useCollectionItems(id);

  // IntersectionObserver sentinel — 뷰포트 진입 시 fetchNextPage
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  /**
   * [목적] 무한 스크롤 sentinel IntersectionObserver 등록
   * [이유] 스크롤 하단 도달 시 자동으로 다음 페이지 요청
   * [사이드 이팩트] hasNextPage=false 이거나 isFetchingNextPage 중이면 fetch 스킵
   * [수정 시 고려사항] sentinel div 가 DOM 에 없으면 observe 하지 않음
   */
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const allItems = sortItems(
    pages.flatMap((p) => p.items),
    sort,
  );

  if (collectionLoading || !collection) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: PAPER1 }}>
        <CollectionCoverSkeleton />
        <Box sx={{ p: '0 16px 28px', display: 'flex', flexDirection: 'column', gap: '9px' }}>
          {[...Array(5)].map((_, i) => (
            <CollectionItemSkeleton key={i} />
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', overflowY: 'auto', bgcolor: PAPER1 }}>
      {/* 커버 */}
      <CollectionCoverSection collection={collection} onBack={() => navigate(-1)} />

      {/* 정렬 헤더 */}
      <Box
        sx={{
          p: '14px 16px 8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography sx={{ fontSize: 11.5, color: INK_HI, fontWeight: 700 }}>
          이 컬렉션의 폴리오
        </Typography>
        {/* B3: 정렬 드롭다운 — BE sort 미지원(ponytail, 파일 헤더 주석) → 클라이언트 정렬 */}
        <Select
          value={sort}
          onChange={(e) => setSort(e.target.value as ItemSortOption)}
          size="small"
          inputProps={{ 'aria-label': '컬렉션 아이템 정렬 기준 선택' }}
          sx={{
            fontSize: 10.5,
            color: INK_MID,
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
          }}
        >
          {(Object.keys(SORT_LABEL) as ItemSortOption[]).map((opt) => (
            <MenuItem key={opt} value={opt} sx={{ fontSize: 11, color: INK_HI }}>
              {SORT_LABEL[opt]}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* 아이템 목록 */}
      <Box sx={{ p: '0 16px 28px', display: 'flex', flexDirection: 'column', gap: '9px' }}>
        {itemsLoading
          ? [...Array(4)].map((_, i) => <CollectionItemSkeleton key={i} />)
          : allItems.map((item, i) => (
              <CollectionItemRow
                key={item.portfolioId}
                item={item}
                index={i}
                onClick={() => navigate(`/folio/${item.portfolioId}`)}
              />
            ))}

        {/* 무한 스크롤 sentinel */}
        <Box ref={sentinelRef} sx={{ height: 1 }} aria-hidden="true" />

        {/* 다음 페이지 로딩 인디케이터 */}
        {isFetchingNextPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: '16px' }}>
            <CircularProgress size={24} aria-label="더 불러오는 중" />
          </Box>
        )}

        {/* 목록 끝 */}
        {!hasNextPage && allItems.length > 0 && (
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: 11,
              color: INK_LOW,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              py: '12px',
            }}
          >
            — 전체 {allItems.length}개 —
          </Typography>
        )}

        {/* 빈 상태 */}
        {!itemsLoading && allItems.length === 0 && (
          <Box sx={{ textAlign: 'center', p: '60px 20px' }}>
            <Typography sx={{ fontSize: 13, color: INK_MID }}>아직 폴리오가 없습니다.</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ─── CollectionCoverSection ────────────────────────────────────────────────────

/**
 * [목적] 컬렉션 커버 영역 — 타이틀 + 큐레이터 + 설명 + 팔로우 버튼
 * [이유] FEED-03 실데이터 기반 커버 렌더링
 * [사이드 이팩트] 컬렉션 팔로우 버튼은 UI 전용 (BE 미구현)
 * [수정 시 고려사항] coverUrl 이 없으면 kind 기반 이모지 폴백 표시. curatorHandle 필드 없음 — curatorId UUID 앞 6자 표시
 */
function CollectionCoverSection({
  collection,
  onBack,
}: {
  collection: CollectionDetail;
  onBack: () => void;
}): JSX.Element {
  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${c.paper[2][L]}, ${c.paper[0][L]})`,
        p: '14px 16px 18px',
        position: 'relative',
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      {/* 내비게이션 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '14px' }}>
        <ButtonBase
          onClick={onBack}
          aria-label="뒤로 가기"
          sx={{
            bgcolor: 'rgba(255,255,255,.7)',
            border: `1px solid ${BORDER}`,
            borderRadius: `${tokens.radius.s}px`,
            width: 30,
            height: 30,
            color: INK_HI,
            fontSize: 14,
            backdropFilter: 'blur(8px)',
            '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
          }}
        >
          ‹
        </ButtonBase>
        <Box sx={{ flex: 1 }} />
      </Box>

      {/* 컬렉션 정보 */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
        <Box
          sx={{
            width: 62,
            height: 62,
            borderRadius: `${tokens.radius.l}px`,
            bgcolor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 32,
            boxShadow: tokens.shadow.card,
            flexShrink: 0,
            overflow: 'hidden',
          }}
          aria-hidden="true"
        >
          {collection.coverUrl ? (
            <Box
              component="img"
              src={collection.coverUrl}
              alt=""
              loading="lazy"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            '🗂'
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, pt: '2px' }}>
          {collection.kind === 'editor' && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                bgcolor: BRAND,
                color: '#fff',
                borderRadius: `${tokens.radius.s}px`,
                px: '7px',
                py: '2px',
                fontSize: 9,
                fontWeight: 700,
                mb: '6px',
                letterSpacing: '0.4px',
              }}
            >
              OFFICIAL
            </Box>
          )}
          <Typography
            component="h1"
            sx={{
              fontSize: 18,
              fontWeight: 800,
              color: INK_HI,
              letterSpacing: '-0.4px',
              lineHeight: 1.3,
              mb: '5px',
            }}
          >
            {collection.title}
          </Typography>
          {collection.summary && (
            <Typography sx={{ fontSize: 11, color: INK_MID, lineHeight: 1.5, mb: '8px' }}>
              {collection.summary}
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '7px',
              fontSize: 10.5,
              color: INK_MID,
            }}
          >
            <Box
              component="span"
              sx={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace', fontWeight: 600 }}
            >
              {collection.curatorId
                ? `큐레이터 #${collection.curatorId.slice(0, 6)}`
                : '강원캔버스'}
            </Box>
            <Box
              component="span"
              aria-hidden="true"
              sx={{ width: 2, height: 2, borderRadius: '50%', bgcolor: INK_LOW }}
            />
            <Box component="span">
              폴리오{' '}
              <Box component="strong" sx={{ color: INK_HI }}>
                {collection.itemCount ?? 0}
              </Box>
              개
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ─── CollectionItemRow ─────────────────────────────────────────────────────────

/**
 * [목적] 컬렉션 아이템 행 — 순번 + 썸네일 + 제목 + 작성자
 * [이유] FEED-03 아이템 목록 개별 행 렌더링
 * [사이드 이팩트] 클릭 시 /folio/:portfolioId 이동
 * [수정 시 고려사항] like onClick 실배선 금지 (후속 fe-folio-social-wire)
 */
function CollectionItemRow({
  item,
  index,
  onClick,
}: {
  item: CollectionItemPreview;
  index: number;
  onClick: () => void;
}): JSX.Element {
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`폴리오 ${item.title ?? '(제목 없음)'} 열기`}
      sx={{
        display: 'flex',
        bgcolor: '#fff',
        borderRadius: `${tokens.radius.m}px`,
        border: `1px solid ${BORDER}`,
        overflow: 'hidden',
        boxShadow: tokens.shadow.card,
        textAlign: 'left',
        alignItems: 'stretch',
        width: '100%',
        '&:hover': { opacity: 0.88 },
        '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
      }}
    >
      {/* 썸네일 */}
      <Box
        sx={{
          width: 84,
          bgcolor: c.paper[2][L],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 32,
          position: 'relative',
          flexShrink: 0,
          overflow: 'hidden',
        }}
        aria-hidden="true"
      >
        {item.coverImageUrl ? (
          <Box
            component="img"
            src={item.coverImageUrl}
            alt=""
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          '🗺'
        )}
        <Box
          sx={{
            position: 'absolute',
            top: 5,
            left: 5,
            fontSize: 8,
            color: '#fff',
            bgcolor: 'rgba(0,0,0,.35)',
            borderRadius: `${tokens.radius.s}px`,
            px: '5px',
            py: '1.5px',
            fontWeight: 700,
            fontFamily: 'JetBrains Mono, ui-monospace, monospace',
          }}
        >
          #{String(index + 1).padStart(2, '0')}
        </Box>
      </Box>

      {/* 텍스트 */}
      <Box
        sx={{
          flex: 1,
          p: '10px 12px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: INK_HI,
            lineHeight: 1.35,
            letterSpacing: '-0.2px',
            mb: '5px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.title ?? '(제목 없음)'}
        </Typography>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: 10, color: INK_MID }}
        >
          <Box component="span" sx={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>
            {item.slug}
          </Box>
        </Box>
      </Box>
    </ButtonBase>
  );
}

// ─── CollectionCoverSkeleton ────────────────────────────────────────────────────

/**
 * [목적] 컬렉션 커버 영역 Skeleton
 * [이유] useCollection 로딩 중 레이아웃 이동 방지
 * [사이드 이팩트] 없음
 * [수정 시 고려사항] 실제 커버 높이와 동기화
 */
function CollectionCoverSkeleton(): JSX.Element {
  return (
    <Box sx={{ p: '14px 16px 18px', borderBottom: `1px solid ${BORDER}` }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '14px', mt: '44px' }}>
        <Skeleton
          variant="rounded"
          width={62}
          height={62}
          sx={{ borderRadius: `${tokens.radius.l}px`, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={24} sx={{ mb: '6px' }} />
          <Skeleton variant="text" height={16} sx={{ mb: '4px' }} />
          <Skeleton variant="text" width="40%" height={14} />
        </Box>
      </Box>
      <Skeleton
        variant="rounded"
        height={42}
        sx={{ mt: '14px', borderRadius: `${tokens.radius.m}px` }}
      />
    </Box>
  );
}
